import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import Lightbox from '../components/Lightbox'

export default function Referencer() {
  const [pageData, setPageData] = useState(null)
  const [galleryItems, setGalleryItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeLightboxIndex, setActiveLightboxIndex] = useState(null)

  useEffect(() => {
    // Fetch page metadata and gallery items in parallel
    Promise.all([
      fetch('https://api.dev.auxo.dk/public/content/pages').then(res => res.json()),
      fetch('https://api.dev.auxo.dk/public/content/gallery').then(res => res.json())
    ])
      .then(([pages, gallery]) => {
        const refPage = pages.find(p => p.page_slug === 'referencer')
        setPageData(refPage)
        
        // Sort gallery items by order
        const sortedGallery = gallery.sort((a, b) => (a.order || 0) - (b.order || 0))
        setGalleryItems(sortedGallery)
        setLoading(false)
      })
      .catch(err => {
        console.error(err)
        setError(err.message)
        setLoading(false)
      })
  }, [])

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <i className="fa-solid fa-spinner animate-spin text-4xl text-brand-orange"></i>
          <p className="text-slate-500 font-medium">Indlæser referencer...</p>
        </div>
      </div>
    )
  }

  // Fallback data matching the scrape exactly if CMS fetch fails
  const fallbackPageData = {
    title: "Referencer",
    meta_title: "Referencer - Trapezdk",
    meta_description: "Se vores referencer og tidligere projekter inden for levering og montering af trapezplader. Vi leverer høj kvalitet i hele processen.",
    content: "<h1>Referencer</h1><p>Her kan du se et udvalg af vores tidligere projekter og referencer inden for levering og montering af selvbærende højprofiler (trapezplader).</p>"
  }

  const fallbackGalleryItems = [
    {
      title: "Ny brandstation Grønland",
      image: ["https://assets.auxodev.dk/assets/28021fd9-fcdc-43f4-860a-5599c796fdc7/6783c0ccbd2c1504a5d95ba6-ny-20brandstation-20gr-c3-b8nland.jpeg"],
      order: 1
    },
    {
      title: "Bolig byggeri København",
      image: ["https://assets.auxodev.dk/assets/28021fd9-fcdc-43f4-860a-5599c796fdc7/6783c193fd5c8a07082b59e4-bolig-20byggeri-20k-c3-b8benhavn.jpg"],
      order: 2
    },
    {
      title: "Tilbygning hancock",
      image: ["https://assets.auxodev.dk/assets/28021fd9-fcdc-43f4-860a-5599c796fdc7/6783c19a62d98a32b73b7ed5-tilbygning-20hancock.jpg"],
      order: 3
    },
    {
      title: "Tts sag - Ribe",
      image: ["https://assets.auxodev.dk/assets/28021fd9-fcdc-43f4-860a-5599c796fdc7/678422f899d34df29745b15c-tts-20sag-20-20ribe.jpg"],
      order: 4
    },
    {
      title: "Trapezmontage Odense",
      image: ["https://assets.auxodev.dk/assets/28021fd9-fcdc-43f4-860a-5599c796fdc7/6784d4b4ca861bada645c1a1-trapezmontage-20odense-20-1.jpg"],
      order: 5
    },
    {
      title: "Arena - Randers",
      image: ["https://assets.auxodev.dk/assets/28021fd9-fcdc-43f4-860a-5599c796fdc7/6784d4ce1fc3db204e897351-arena-20-20randers.jpg"],
      order: 6
    },
    {
      title: "TTS sag",
      image: ["https://assets.auxodev.dk/assets/28021fd9-fcdc-43f4-860a-5599c796fdc7/6784d4ee8815f9c670344925-tts-20sag.jpg"],
      order: 7
    },
    {
      title: "Trapez - Give",
      image: ["https://assets.auxodev.dk/assets/28021fd9-fcdc-43f4-860a-5599c796fdc7/6784d516d99ca1560a1e3046-trapez-20-20give.jpg"],
      order: 8
    }
  ]

  const data = pageData || fallbackPageData
  const items = galleryItems.length > 0 ? galleryItems : fallbackGalleryItems

  const handlePrev = () => {
    setActiveLightboxIndex(prev => (prev === 0 ? items.length - 1 : prev - 1))
  }

  const handleNext = () => {
    setActiveLightboxIndex(prev => (prev === items.length - 1 ? 0 : prev + 1))
  }

  return (
    <div className="w-full">
      <Helmet>
        <title>{data.meta_title || "Referencer - Trapezdk"}</title>
        <meta name="description" content={data.meta_description || "Se vores referencer og tidligere projekter inden for levering og montering af trapezplader."} />
        <meta property="og:title" content={data.meta_title || "Referencer - Trapezdk"} />
        <meta property="og:description" content={data.meta_description || "Se vores referencer og tidligere projekter inden for levering og montering af trapezplader."} />
        <meta property="og:image" content={items[0]?.image?.[0] || ""} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      {/* Page Header */}
      <section className="relative text-white py-24 lg:py-32 overflow-hidden bg-slate-950">
        {/* Background Image with Dark Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/28021fd9-fcdc-43f4-860a-5599c796fdc7/6784d4ee8815f9c670344925-tts-20sag.jpg" 
            alt="Trapezplader baggrund" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-900/85 to-slate-950/80"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10 text-left">
          <span className="text-brand-orange font-bold text-sm tracking-wider uppercase">Vores Resultater</span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mt-2">Referencer</h1>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-12 bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
            Her kan du se et udvalg af vores tidligere projekter og referencer inden for levering og montering af selvbærende højprofiler (trapezplader).
          </p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {items.map((item, index) => (
              <div 
                key={index}
                onClick={() => setActiveLightboxIndex(index)}
                className="group bg-white rounded-none overflow-hidden shadow-sm hover:shadow-xl border border-slate-100 transition-all duration-300 cursor-pointer flex flex-col h-full"
              >
                {/* Image Container */}
                <div className="relative overflow-hidden aspect-[4/3] bg-slate-100 shrink-0">
                  <img 
                    src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,height=450,fit=cover,quality=80,format=auto/${item.image[0].replace('https://assets.auxodev.dk/', '')}`} 
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-brand-blue/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center text-white text-xl shadow-lg border border-white/30 transform scale-90 group-hover:scale-100 transition-transform duration-300">
                      <i className="fa-solid fa-magnifying-glass-plus"></i>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5 flex-grow flex flex-col justify-between text-left">
                  <h3 className="font-bold text-slate-900 group-hover:text-brand-orange transition-colors text-base sm:text-lg line-clamp-2 leading-snug">
                    {item.title}
                  </h3>
                  <div className="flex items-center gap-1.5 text-brand-orange text-xs font-semibold uppercase tracking-wider mt-4">
                    <span>Se projekt</span>
                    <i className="fa-solid fa-arrow-right text-[10px] transition-transform duration-300 group-hover:translate-x-1"></i>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Component */}
      {activeLightboxIndex !== null && (
        <Lightbox 
          images={items}
          currentIndex={activeLightboxIndex}
          onClose={() => setActiveLightboxIndex(null)}
          onPrev={handlePrev}
          onNext={handleNext}
        />
      )}
    </div>
  )
}
